import { Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export enum PieceType {
  I = 'I',
  O = 'O', 
  T = 'T',
  S = 'S',
  Z = 'Z',
  J = 'J',
  L = 'L'
}

interface PieceDefinition {
  shapes: number[][][]; // All rotation states
  color: string;
  size: number;
}

export class Piece {
  private type: PieceType;
  private position: Vector2;
  private rotationState: number = 0;
  private renderManager: RenderManager;
  private audioManager: AudioManager;

  // Standard Tetris piece definitions with all rotation states
  private static readonly PIECE_DEFINITIONS: { [key in PieceType]: PieceDefinition } = {
    [PieceType.I]: {
      shapes: [
        [
          [0,0,0,0],
          [1,1,1,1],
          [0,0,0,0],
          [0,0,0,0]
        ],
        [
          [0,0,1,0],
          [0,0,1,0],
          [0,0,1,0],
          [0,0,1,0]
        ],
        [
          [0,0,0,0],
          [0,0,0,0],
          [1,1,1,1],
          [0,0,0,0]
        ],
        [
          [0,1,0,0],
          [0,1,0,0],
          [0,1,0,0],
          [0,1,0,0]
        ]
      ],
      color: '#00FFFF', // Cyan
      size: 4
    },
    [PieceType.O]: {
      shapes: [
        [
          [1,1],
          [1,1]
        ]
      ],
      color: '#FFFF00', // Yellow
      size: 2
    },
    [PieceType.T]: {
      shapes: [
        [
          [0,1,0],
          [1,1,1],
          [0,0,0]
        ],
        [
          [0,1,0],
          [0,1,1],
          [0,1,0]
        ],
        [
          [0,0,0],
          [1,1,1],
          [0,1,0]
        ],
        [
          [0,1,0],
          [1,1,0],
          [0,1,0]
        ]
      ],
      color: '#AA00FF', // Purple
      size: 3
    },
    [PieceType.S]: {
      shapes: [
        [
          [0,1,1],
          [1,1,0],
          [0,0,0]
        ],
        [
          [0,1,0],
          [0,1,1],
          [0,0,1]
        ],
        [
          [0,0,0],
          [0,1,1],
          [1,1,0]
        ],
        [
          [1,0,0],
          [1,1,0],
          [0,1,0]
        ]
      ],
      color: '#00FF00', // Green
      size: 3
    },
    [PieceType.Z]: {
      shapes: [
        [
          [1,1,0],
          [0,1,1],
          [0,0,0]
        ],
        [
          [0,0,1],
          [0,1,1],
          [0,1,0]
        ],
        [
          [0,0,0],
          [1,1,0],
          [0,1,1]
        ],
        [
          [0,1,0],
          [1,1,0],
          [1,0,0]
        ]
      ],
      color: '#FF0000', // Red
      size: 3
    },
    [PieceType.J]: {
      shapes: [
        [
          [1,0,0],
          [1,1,1],
          [0,0,0]
        ],
        [
          [0,1,1],
          [0,1,0],
          [0,1,0]
        ],
        [
          [0,0,0],
          [1,1,1],
          [0,0,1]
        ],
        [
          [0,1,0],
          [0,1,0],
          [1,1,0]
        ]
      ],
      color: '#0000FF', // Blue
      size: 3
    },
    [PieceType.L]: {
      shapes: [
        [
          [0,0,1],
          [1,1,1],
          [0,0,0]
        ],
        [
          [0,1,0],
          [0,1,0],
          [0,1,1]
        ],
        [
          [0,0,0],
          [1,1,1],
          [1,0,0]
        ],
        [
          [1,1,0],
          [0,1,0],
          [0,1,0]
        ]
      ],
      color: '#FF8800', // Orange
      size: 3
    }
  };

  constructor(
    type: PieceType,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager
  ) {
    this.type = type;
    this.position = { x, y };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.rotationState = 0;
  }

  public getType(): PieceType {
    return this.type;
  }

  public getPosition(): Vector2 {
    return { ...this.position };
  }

  public setPosition(x: number, y: number): void {
    this.position.x = x;
    this.position.y = y;
  }

  public move(deltaX: number, deltaY: number): void {
    this.position.x += deltaX;
    this.position.y += deltaY;
  }

  public rotate(): void {
    const definition = Piece.PIECE_DEFINITIONS[this.type];
    this.rotationState = (this.rotationState + 1) % definition.shapes.length;
  }

  public getShape(): number[][] {
    const definition = Piece.PIECE_DEFINITIONS[this.type];
    return definition.shapes[this.rotationState];
  }

  public getColor(): string {
    return Piece.PIECE_DEFINITIONS[this.type].color;
  }

  public getSize(): number {
    return Piece.PIECE_DEFINITIONS[this.type].size;
  }

  public getBoundingBox(): { minX: number; maxX: number; minY: number; maxY: number } {
    const shape = this.getShape();
    let minX = shape[0].length;
    let maxX = -1;
    let minY = shape.length;
    let maxY = -1;

    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        }
      }
    }

    return {
      minX: this.position.x + minX,
      maxX: this.position.x + maxX,
      minY: this.position.y + minY,
      maxY: this.position.y + maxY
    };
  }

  public clone(): Piece {
    const cloned = new Piece(
      this.type,
      this.position.x,
      this.position.y,
      this.renderManager,
      this.audioManager
    );
    cloned.rotationState = this.rotationState;
    return cloned;
  }

  public getOriginalType(): Piece {
    return new Piece(
      this.type,
      0,
      0,
      this.renderManager,
      this.audioManager
    );
  }

  // Get all filled positions relative to the piece's position
  public getFilledPositions(): Vector2[] {
    const positions: Vector2[] = [];
    const shape = this.getShape();

    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          positions.push({
            x: this.position.x + x,
            y: this.position.y + y
          });
        }
      }
    }

    return positions;
  }

  // Static method to get a random piece type
  public static getRandomType(): PieceType {
    const types = Object.values(PieceType);
    return types[Math.floor(Math.random() * types.length)];
  }

  // Static method to create a new piece of a specific type
  public static create(
    type: PieceType,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager
  ): Piece {
    return new Piece(type, x, y, renderManager, audioManager);
  }
}